import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { ArrowLeft, ArrowRight, Clock, Video, MapPin, Loader2 } from "lucide-react";
import { PageHero } from "@/components/site/PageHero";
import { MedicalDisclaimer } from "@/components/site/MedicalDisclaimer";
import { Reveal } from "@/components/motion/Reveal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { API } from "@/lib/api";

// One recommended consultation type per top-level category, shown in the booking
// finder. Individual topics (46 across the 4 categories) are no longer offered
// here — the intake questionnaire captures that level of detail instead.
const CATEGORY_RECOMMEND = {
  "hormonal-health": "hormone",
  "fertility-pregnancy": "fertility",
  "menopause-aging": "hormone",
  "preventive-medicine": "nutrition",
};

const STEPS = ["type", "time", "details", "confirm"];

export default function Book() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const categories = [
    { slug: "hormonal-health", label: t("nav.hormonalHealth") },
    { slug: "fertility-pregnancy", label: t("nav.fertilityPregnancy") },
    { slug: "menopause-aging", label: t("nav.menopauseAging") },
    { slug: "preventive-medicine", label: t("nav.preventiveMedicine") },
  ];

  const [types, setTypes] = useState([]);
  const [currency, setCurrency] = useState("EUR");
  const [days, setDays] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(true);
  const [bookingEnabled, setBookingEnabled] = useState(null);

  const [step, setStep] = useState(0);
  const [lifeStage, setLifeStage] = useState("");
  const [typeId, setTypeId] = useState("");
  const [format, setFormat] = useState("online");
  const [dateSel, setDateSel] = useState("");
  const [slot, setSlot] = useState("");
  const [patient, setPatient] = useState({ name: "", email: "", phone: "" });
  const [concern, setConcern] = useState("");
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/consultation-types`).then(({ data }) => {
      setTypes(Array.isArray(data.types) ? data.types : []);
      setCurrency(((data.currency || "eur").toUpperCase()) || "EUR");
    }).catch(() => setTypes([]));
    axios.get(`${API}/availability?days=21`)
      .then(({ data }) => {
        setBookingEnabled(data.booking_enabled !== false);
        setDays(data.days || []);
      })
      .catch(() => setBookingEnabled(false))
      .finally(() => setLoadingSlots(false));
  }, []);

  const selectedType = useMemo(() => types.find((x) => x.id === typeId), [types, typeId]);
  const money = (n) => moneyFmt(n);
  const moneyFmt = (n) => {
    const v = Number(n) || 0;
    try {
      return new Intl.NumberFormat(i18n.language, { style: "currency", currency: currency || "EUR", maximumFractionDigits: 0 }).format(v);
    } catch {
      return `${v} ${currency || "EUR"}`;
    }
  };
  const fmtDay = (iso) => new Date(iso).toLocaleDateString(i18n.language, { weekday: "short", day: "numeric", month: "short" });
  const fmtTime = (iso) => new Date(iso).toLocaleTimeString(i18n.language, { hour: "2-digit", minute: "2-digit" });
  const fmtFull = (iso) => new Date(iso).toLocaleString(i18n.language, { weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" });

  const pickType = (id) => {
    setTypeId(id);
    const tf = types.find((x) => x.id === id)?.format;
    setFormat(tf === "online" ? "online" : "in_person");
    setStep(1);
  };

  const slotsForDate = days.find((d) => d.date === dateSel)?.slots || [];

  const validateDetails = () => {
    const e = {};
    if (!patient.name.trim()) e.name = t("booking.form.required");
    if (!patient.email.trim()) e.email = t("booking.form.required");
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(patient.email)) e.email = t("booking.form.invalidEmail");
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const confirm = async () => {
    if (bookingEnabled === false) return toast.error(t("booking.closed.text"));
    setSubmitting(true);
    try {
      const { data: booking } = await axios.post(`${API}/bookings`, {
        type_id: typeId, slot, patient, life_stage: lifeStage, concern,
        format, with_deposit: false,
        language: String(i18n.resolvedLanguage || i18n.language || "en").split("-")[0],
      });
      navigate(`/booking/requested?booking_id=${booking.id}`);
    } catch (err) {
      const msg = err?.response?.status === 503
        ? t("booking.closed.text")
        : err?.response?.status === 409 ? t("booking.labels.noSlots") : t("booking.form.privacy");
      toast.error(msg);
      setSubmitting(false);
    }
  };

  const tt = (id, k) => t(`booking.types.${id}.${k}`);
  const loc = (ty, field) => {
    const key = field === "title" ? "name" : field;
    return ty.i18n?.[i18n.language]?.[field] || ty.i18n?.en?.[field] || tt(ty.id, key);
  };

  return (
    <div data-testid="book-page">
      <Toaster position="top-center" />
      <PageHero kicker={t("booking.kicker")} title={t("booking.title")} text={t("booking.text")} />

      <section className="bg-warm-ivory">
        <div className="mx-auto max-w-5xl px-5 pb-24 lg:px-10">
          {bookingEnabled === null ? (
            <p className="flex items-center justify-center gap-2 py-16 text-deep-ink/60" data-testid="booking-status-loading">
              <Loader2 className="h-5 w-5 animate-spin" />{t("booking.labels.loadingSlots")}
            </p>
          ) : bookingEnabled === false ? (
            <Reveal>
              <div className="mx-auto max-w-2xl rounded-3xl border border-champagne-gold/40 bg-sand p-8 text-center" data-testid="booking-disabled">
                <h2 className="font-serif text-3xl text-deep-ink">{t("booking.closed.title")}</h2>
                <p className="mt-4 text-base leading-relaxed text-deep-ink/70">{t("booking.closed.text")}</p>
              </div>
            </Reveal>
          ) : (
            <>
          {/* Stepper */}
          <div className="mb-10 flex flex-wrap items-center gap-x-6 gap-y-2">
            {STEPS.map((s, i) => (
              <div key={s} className="flex items-center gap-2" data-testid={`step-indicator-${s}`}>
                <span className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-medium ${
                  i < step ? "bg-champagne-gold text-deep-ink" : i === step ? "bg-deep-ink text-warm-ivory" : "bg-sand text-deep-ink/50"
                }`}>
                  {i < step ? <Check className="h-4 w-4" /> : i + 1}
                </span>
                <span className={`text-sm ${i === step ? "text-deep-ink" : "text-deep-ink/50"}`}>
                  {t(`booking.steps.${s}`)}
                </span>
              </div>
            ))}
          </div>

          {/* STEP 0: Type + finder */}
          {step === 0 && (
            <Reveal>
              <div className="mb-8 max-w-md">
                <Label className="mb-2 block text-sm text-deep-ink/70">{t("booking.finder.lifeStageLabel")}</Label>
                <Select value={lifeStage} onValueChange={setLifeStage}>
                  <SelectTrigger data-testid="finder-lifestage" className="bg-sand"><SelectValue placeholder={t("booking.finder.lifeStagePlaceholder")} /></SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => (
                      <SelectItem key={c.slug} value={c.slug}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <h2 className="mb-5 font-serif text-2xl text-deep-ink">{t("booking.labels.chooseType")}</h2>
              <div className="grid gap-4 md:grid-cols-2">
                {types.map((ty) => {
                  const rec = lifeStage && CATEGORY_RECOMMEND[lifeStage] === ty.id;
                  return (
                    <button
                      key={ty.id}
                      data-testid={`type-card-${ty.id}`}
                      onClick={() => pickType(ty.id)}
                      className={`group relative flex flex-col rounded-3xl border p-6 text-start transition-colors ${
                        rec ? "border-champagne-gold bg-sand" : "border-border bg-warm-ivory hover:border-champagne-gold"
                      }`}
                    >
                      {rec && (
                        <span className="absolute end-4 top-4 rounded-full bg-champagne-gold px-3 py-1 text-xs font-medium text-deep-ink">
                          {t("booking.finder.recommended")}
                        </span>
                      )}
                      <h3 className="font-serif text-xl text-deep-ink">{loc(ty, "title")}</h3>
                      <p className="mt-2 flex-1 text-sm leading-relaxed text-deep-ink/70">{loc(ty, "description")}</p>
                      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-deep-ink/60">
                        <span className="inline-flex items-center gap-1"><Clock className="h-4 w-4" />{t("booking.labels.minutes", { count: ty.duration })}</span>
                        <span className="inline-flex items-center gap-1">
                          {ty.format === "online" ? <Video className="h-4 w-4" /> : <MapPin className="h-4 w-4" />}
                          {t(`booking.formats.${ty.format}`)}
                        </span>
                        {ty.fee > 0 && <span className="font-medium text-deep-ink">{money(ty.fee)}</span>}
                      </div>
                    </button>
                  );
                })}
              </div>
              <p className="mt-6 text-sm text-deep-ink/50">{t("booking.feeNote")}</p>
            </Reveal>
          )}

          {/* STEP 1: Time + format */}
          {step === 1 && selectedType && (
            <Reveal>
              {selectedType.format === "both" && (
                <div className="mb-8">
                  <h2 className="mb-3 font-serif text-2xl text-deep-ink">{t("booking.labels.chooseFormat")}</h2>
                  <div className="flex gap-3">
                    {["online", "in_person"].map((f) => (
                      <button key={f} data-testid={`format-${f}`} onClick={() => setFormat(f)}
                        className={`inline-flex items-center gap-2 rounded-full border px-5 py-2.5 text-sm transition-colors ${
                          format === f ? "border-champagne-gold bg-sand text-deep-ink" : "border-border text-deep-ink/70 hover:border-champagne-gold"
                        }`}>
                        {f === "online" ? <Video className="h-4 w-4" /> : <MapPin className="h-4 w-4" />}
                        {t(`booking.formats.${f}`)}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <h2 className="mb-4 font-serif text-2xl text-deep-ink">{t("booking.labels.chooseTime")}</h2>
              {loadingSlots ? (
                <p className="flex items-center gap-2 text-deep-ink/60"><Loader2 className="h-4 w-4 animate-spin" />{t("booking.labels.loadingSlots")}</p>
              ) : days.length === 0 ? (
                <p className="text-deep-ink/60">{t("booking.labels.noSlots")}</p>
              ) : (
                <>
                  <div className="mb-6 flex flex-wrap gap-2">
                    {days.map((d) => (
                      <button key={d.date} data-testid={`date-${d.date}`} onClick={() => { setDateSel(d.date); setSlot(""); }}
                        className={`rounded-2xl border px-4 py-3 text-sm transition-colors ${
                          dateSel === d.date ? "border-champagne-gold bg-sand" : "border-border hover:border-champagne-gold"
                        }`}>
                        {fmtDay(d.date)}
                      </button>
                    ))}
                  </div>
                  {dateSel && (
                    <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-6">
                      {slotsForDate.map((s) => (
                        <button key={s} data-testid={`slot-${s}`} onClick={() => setSlot(s)}
                          className={`rounded-xl border px-3 py-2.5 text-sm transition-colors ${
                            slot === s ? "border-champagne-gold bg-champagne-gold/20 text-deep-ink" : "border-border text-deep-ink/70 hover:border-champagne-gold"
                          }`}>
                          {fmtTime(s)}
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}

              <StepNav onBack={() => setStep(0)} onNext={() => setStep(2)} nextDisabled={!slot} tBack={t("booking.labels.back")} tNext={t("booking.labels.continue")} />
            </Reveal>
          )}

          {/* STEP 2: Details */}
          {step === 2 && (
            <Reveal>
              <h2 className="mb-5 font-serif text-2xl text-deep-ink">{t("booking.form.title")}</h2>
              <div className="grid max-w-2xl gap-5">
                <Field label={t("booking.form.name")} error={errors.name}>
                  <Input data-testid="book-name" value={patient.name} onChange={(e) => setPatient({ ...patient, name: e.target.value })} className="bg-sand" />
                </Field>
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field label={t("booking.form.email")} error={errors.email}>
                    <Input data-testid="book-email" type="email" value={patient.email} onChange={(e) => setPatient({ ...patient, email: e.target.value })} className="bg-sand" />
                  </Field>
                  <Field label={t("booking.form.phone")}>
                    <Input data-testid="book-phone" value={patient.phone} onChange={(e) => setPatient({ ...patient, phone: e.target.value })} className="bg-sand" />
                  </Field>
                </div>
                <Field label={t("booking.form.concern")}>
                  <Textarea data-testid="book-concern" rows={3} value={concern} onChange={(e) => setConcern(e.target.value)} placeholder={t("booking.form.concernPlaceholder")} className="bg-sand" />
                </Field>
                <p className="text-sm text-deep-ink/50">{t("booking.form.privacy")}</p>
              </div>
              <StepNav onBack={() => setStep(1)} onNext={() => { if (validateDetails()) setStep(3); }} tBack={t("booking.labels.back")} tNext={t("booking.labels.continue")} />
            </Reveal>
          )}

          {/* STEP 3: Review and send request */}
          {step === 3 && selectedType && (
            <Reveal>
              <div className="grid gap-8 lg:grid-cols-[1.1fr_1fr]">
                <div>
                  <h2 className="mb-4 font-serif text-2xl text-deep-ink">{t("booking.payment.title")}</h2>
                  <p className="mb-6 text-base leading-relaxed text-deep-ink/70">{t("booking.payment.text")}</p>
                  <div className="mt-6"><MedicalDisclaimer /></div>
                </div>

                <div className="h-fit rounded-3xl bg-sand p-7">
                  <h3 className="mb-4 font-serif text-xl text-deep-ink">{t("booking.summary.title")}</h3>
                  <Row label={t("booking.summary.type")} value={loc(selectedType, "title")} />
                  <Row label={t("booking.summary.when")} value={fmtFull(slot)} />
                  <Row label={t("booking.summary.format")} value={t(`booking.formats.${format}`)} />
                  {selectedType.fee > 0 && <Row label={t("booking.summary.fee")} value={money(selectedType.fee)} />}
                  <Button data-testid="confirm-booking" onClick={confirm} disabled={submitting}
                    className="mt-6 w-full rounded-full bg-deep-ink py-6 text-warm-ivory hover:bg-champagne-gold hover:text-deep-ink">
                    {submitting ? t("booking.summary.processing") : t("booking.summary.reserveButton")}
                  </Button>
                  <button onClick={() => setStep(2)} className="mt-4 inline-flex items-center gap-2 text-sm text-champagne-gold">
                    <ArrowLeft className="rtl-flip h-4 w-4" />{t("booking.labels.back")}
                  </button>
                </div>
              </div>
            </Reveal>
          )}
            </>
          )}
        </div>
      </section>
    </div>
  );
}

const Field = ({ label, error, children }) => (
  <div>
    <Label className="mb-1.5 block text-sm text-deep-ink/70">{label}</Label>
    {children}
    {error && <p className="mt-1 text-sm text-terracotta">{error}</p>}
  </div>
);

const StepNav = ({ onBack, onNext, nextDisabled, tBack, tNext }) => (
  <div className="mt-10 flex items-center justify-between">
    <button onClick={onBack} className="inline-flex items-center gap-2 text-sm font-medium text-deep-ink/70 hover:text-champagne-gold">
      <ArrowLeft className="rtl-flip h-4 w-4" />{tBack}
    </button>
    <Button onClick={onNext} disabled={nextDisabled} data-testid="step-next"
      className="rounded-full bg-deep-ink px-7 text-warm-ivory hover:bg-champagne-gold hover:text-deep-ink">
      {tNext}<ArrowRight className="rtl-flip ms-1 h-4 w-4" />
    </Button>
  </div>
);

const Row = ({ label, value }) => (
  <div className="flex items-start justify-between gap-4 border-b border-border/60 py-3 last:border-0">
    <span className="text-sm text-deep-ink/60">{label}</span>
    <span className="text-end text-sm font-medium text-deep-ink">{value}</span>
  </div>
);
